import { Datetime, Schema } from '@/utils';
import {
  IncidentIssueDocument,
  IncidentIssuePropertyDamageEnum,
  IncidentIssueWithCache,
  IncidentIssueWithCacheCrewInvolvedEnum,
  IncidentIssueWithCacheIncidentOccuranceEnum,
  Trip,
} from '@/models/gen/graphql';
import { clearFormStorage, getFormStorage, setFormStorage } from '@/common/Form';

export namespace IncidentHistoryFormDefinitions {
  export type Locale =
    | 'SUBMIT_BUTTON_TEXT'
    | 'SUBMIT_BUTTON_COLOR'
    | 'Incident'
    | 'Description'
    | 'Station'
    | 'Driver Name'
    | 'Van Number'
    | 'Employee ID'
    | 'Samsara Camera Working?'
    | 'Manager On Duty'
    | 'Were Crew Onboard?'
    | 'Anyone Injured?'
    | 'Medical Attention Needed?'
    | 'Dispatcher Name'
    | 'Incident Date/Time'
    | 'Recovery Flight #'
    | 'Recovery FLT Date/Time'
    | 'Crew Info'
    | 'Enter crew members name(s), phone(s), email(s)'
    | 'Recovery Info'
    | 'What Type Of Medical Attention Was Offered/Required'
    | 'Property Damage?'
    | 'Where Did This Occur?'
    | 'Occurence Location?'
    | 'Describe Damage'
    | 'Witnesses'
    | 'Enter witnesses name(s), phone(s), email(s)'
    | 'Create Incident'
    | 'Incident History'
    | 'Must be a positive number'
    | 'Must be exactly 2 characters'
    | 'Must be exactly 4 characters'
    | 'Must be exactly 5 characters'
    | 'Must not exceed 256 characters'
    | 'Must not exceed 512 characters'
    | 'Must not exceed 64 characters'
    | 'Required';
  export type Value = Partial<IncidentIssueWithDocuments>;
  export type Props = {
    onSubmit?: (data: Value) => void | Value | Promise<Value> | undefined;
    onEditIncident?: (data: Value) => void | Value | Promise<Value> | undefined;
    locale?: Partial<Record<Locale, string>>;
    value?: Partial<Value>;
    trip?: Partial<Trip>;
  };
  export const initValue: Value = {
    crewInfo: '',
    crewInvolved: undefined,
    description: '',
    dispatcherId: '',
    driverId: '',
    incidentDatetime: '',
    incidentOccurance: undefined,
    incidentOccuranceOther: '',
    incidentWitnesses: '',
    managerId: '',
    medicalAttentionType: '',
    medicalInjuries: undefined,
    medicalNeeded: undefined,
    outboundDatetime: '',
    propertyDamage: undefined,
    propertyDamageDescription: '',
    recoveryInfo: '',
    samsaraCameraWorking: undefined,
    station: '',
    tripId: '',
    vehicleId: '',
    outboundFlightName: undefined,
    outboundFlightNumber: undefined,
  };
  export const getLocale = (value: Value): Partial<Record<Locale, string>> => ({
    SUBMIT_BUTTON_TEXT: value.id ? 'Save Incident' : 'Create Incident',
    SUBMIT_BUTTON_COLOR: value.id ? 'primary' : 'success',
  });
  export const validator = (locale: (original: Locale) => string) =>
    Schema((z) =>
      z.object({
        crewInfo: z.string().min(1, { message: locale('Required') }),
        crewInvolved: z.nativeEnum(IncidentIssueWithCacheCrewInvolvedEnum, { message: locale('Required') }),
        description: z.string().min(1, { message: locale('Required') }),
        dispatcherId: z.string().uuid({ message: locale('Required') }),
        driverId: z.string().uuid({ message: locale('Required') }),
        incidentDatetime: z
          .string()
          .refine((val: string): boolean => !!val && !!new Datetime(val).toString(), { message: locale('Required') }),
        incidentOccurance: z.nativeEnum(IncidentIssueWithCacheIncidentOccuranceEnum, { message: locale('Required') }),
        incidentOccuranceOther: z.string().min(1, { message: locale('Required') }),
        incidentWitnesses: z.string().min(1, { message: locale('Required') }),
        managerId: z.string().uuid({ message: locale('Required') }),
        outboundDatetime: z.string().refine((val: string): boolean => !!val && !!new Datetime(val).toString()),
        outboundFlightNumber: z.string().max(4, { message: locale('Must be exactly 4 characters') }),
        outboundFlightName: z.string().max(64, { message: locale('Must not exceed 64 characters') }),
        propertyDamage: z.nativeEnum(IncidentIssuePropertyDamageEnum, { message: locale('Required') }),
        propertyDamageDescription: z.string().min(1, { message: locale('Required') }),
        recoveryInfo: z.string().min(1, { message: locale('Required') }),
        station: z.string().min(1, { message: locale('Required') }),
        tripId: z.string().uuid({ message: locale('Required') }),
        vehicleId: z.string().uuid({ message: locale('Required') }),
      })
    );
  export const searchable = {
    station: (val) => val,
    incidentDatetime: (val) => val,
    tripServicerIataAirlineCode: (val) => val,
    tripFlightNumber: (val) => val,
    tripScheduled: (val) => val,
    vehicleTrackingId: (val) => val,
    driverFirstName: (val, full) => [val, full?.driverLastName || ''].join(' ').trim(),
    driverEmployeeId: (val) => val,
    safetyFirstName: (val, full) => [val, full?.safetyLastName || ''].join(' ').trim(),
    medicalInjuries: (val) => val,
    crewInvolved: (val) => val,
  };
  export const saveForm = (values: Value): void => setFormStorage('IncidentHistoryForm', values);
  export const loadForm = (): Value => getFormStorage('IncidentHistoryForm');
  export const clearForm = (): void => clearFormStorage('IncidentHistoryForm');
}
export type IncidentIssueWithDocuments = IncidentIssueWithCache & {
  documents: IncidentIssueDocument[];
};
